import autoTable from 'jspdf-autotable';
import { formatDateString, formatMoney } from 'lib/functions/utils';

import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';
import { ExtensionReportData, ExtensionReportFieldVisibility } from './types';

interface ExtensionReportConfig extends PDFGeneratorConfig {
  extension: ExtensionReportData;
  fieldVisibility?: ExtensionReportFieldVisibility;
}

type FieldVisibilityKey = keyof ExtensionReportFieldVisibility;

export class ExtensionReportGenerator extends BasePDFGenerator {
  private extension: ExtensionReportData;
  private fieldVisibility: Required<ExtensionReportFieldVisibility>;
  private summaryBottomY = 120;

  constructor(config: ExtensionReportConfig) {
    super(config);
    this.extension = config.extension;
    this.fieldVisibility = {
      showDocumentInfo: config.fieldVisibility?.showDocumentInfo !== false,
      showExtensionInfo: config.fieldVisibility?.showExtensionInfo !== false,
      showDates: config.fieldVisibility?.showDates !== false,
      showParamsAndCosts: config.fieldVisibility?.showParamsAndCosts !== false,
      showObservation: config.fieldVisibility?.showObservation !== false,
      showDocumentFinancials:
        config.fieldVisibility?.showDocumentFinancials !== false,
      showPendingAccounts:
        config.fieldVisibility?.showPendingAccounts !== false,
    };
  }

  private shouldShowField(fieldName: FieldVisibilityKey): boolean {
    return this.fieldVisibility[fieldName] === true;
  }

  private formatPendingAccountDate(
    value: string | Date | null | undefined,
  ): string {
    if (value == null || value === '') return '-';
    try {
      return formatDateString(String(value), 'dd-mm-yyyy');
    } catch {
      return '-';
    }
  }

  private drawKeyValueIfVisible(
    label: string,
    value: string,
    x: number,
    y: number,
    valueOffset: number,
    fieldName: FieldVisibilityKey,
  ): number {
    if (this.shouldShowField(fieldName)) {
      this.drawKeyValue(label, value, x, y, valueOffset);
      return y + 5;
    }
    return y;
  }

  protected addSummarySection(): void {
    const col1X = 10;
    const col2X = 110;
    const valueOffset = 45;
    const initialY = 35;

    let col1Y = initialY;
    let col2Y = initialY;

    if (this.shouldShowField('showDocumentInfo')) {
      if (this.extension.folio !== undefined) {
        col1Y = this.drawKeyValueIfVisible(
          'Folio documento',
          this.extension.folio,
          col1X,
          col1Y,
          valueOffset,
          'showDocumentInfo',
        );
      }
      if (this.extension.businessName !== undefined) {
        col1Y = this.drawKeyValueIfVisible(
          'Cliente',
          getShortString(this.extension.businessName, 28),
          col1X,
          col1Y,
          valueOffset,
          'showDocumentInfo',
        );
      }
      if (this.extension.businessIdentifier !== undefined) {
        col1Y = this.drawKeyValueIfVisible(
          'Rut cliente',
          this.extension.businessIdentifier,
          col1X,
          col1Y,
          valueOffset,
          'showDocumentInfo',
        );
      }
      if (this.extension.stakeholderName !== undefined) {
        col2Y = this.drawKeyValueIfVisible(
          'Deudor',
          getShortString(this.extension.stakeholderName, 28),
          col2X,
          col2Y,
          valueOffset,
          'showDocumentInfo',
        );
      }
      if (this.extension.stakeholderIdentifier !== undefined) {
        col2Y = this.drawKeyValueIfVisible(
          'Rut deudor',
          this.extension.stakeholderIdentifier,
          col2X,
          col2Y,
          valueOffset,
          'showDocumentInfo',
        );
      }
    }

    if (this.shouldShowField('showExtensionInfo')) {
      col1Y = this.drawKeyValueIfVisible(
        'ID prórroga',
        String(this.extension.id),
        col1X,
        col1Y,
        valueOffset,
        'showExtensionInfo',
      );
      const status = this.extension.statusEsp ?? this.extension.status ?? 'N/A';
      col2Y = this.drawKeyValueIfVisible(
        'Estado',
        status,
        col2X,
        col2Y,
        valueOffset,
        'showExtensionInfo',
      );
    }

    const maxInfoY = Math.max(col1Y, col2Y);
    let datesY = maxInfoY + 8;

    if (this.shouldShowField('showDates')) {
      this.drawText('Fechas', col1X, datesY, { size: 12, bold: true });
      datesY += 8;
      datesY = this.drawKeyValueIfVisible(
        'Fecha inicio',
        formatDateString(this.extension.startDate as string, 'dd-mm-yyyy'),
        col1X,
        datesY,
        valueOffset,
        'showDates',
      );
      datesY = this.drawKeyValueIfVisible(
        'Fecha vencimiento',
        formatDateString(this.extension.dueDate as string, 'dd-mm-yyyy'),
        col1X,
        datesY,
        valueOffset,
        'showDates',
      );
      datesY = this.drawKeyValueIfVisible(
        'Fecha creación',
        formatDateString(this.extension.createdAt as string, 'dd-mm-yyyy'),
        col2X,
        datesY - 10,
        valueOffset,
        'showDates',
      );
      datesY += 5;
    }

    let paramsY = datesY + 5;

    if (this.shouldShowField('showParamsAndCosts')) {
      this.drawText('Parámetros y costos', col1X, paramsY, {
        size: 12,
        bold: true,
      });
      paramsY += 8;
      paramsY = this.drawKeyValueIfVisible(
        'Tasa',
        `${this.extension.rate}%`,
        col1X,
        paramsY,
        valueOffset,
        'showParamsAndCosts',
      );
      paramsY = this.drawKeyValueIfVisible(
        'Costo tasa',
        formatMoney(this.extension.rateCost),
        col1X,
        paramsY,
        valueOffset,
        'showParamsAndCosts',
      );
      paramsY = this.drawKeyValueIfVisible(
        'Costo fijo',
        formatMoney(this.extension.operationCostFixed),
        col1X,
        paramsY,
        valueOffset,
        'showParamsAndCosts',
      );
      paramsY = this.drawKeyValueIfVisible(
        'Costo variable',
        formatMoney(this.extension.operationCostVariable),
        col1X,
        paramsY,
        valueOffset,
        'showParamsAndCosts',
      );
      const rateVar =
        this.extension.operationCostRateVariable != null
          ? `${this.extension.operationCostRateVariable}%`
          : '-';
      this.drawKeyValueIfVisible(
        'Tasa costo variable',
        rateVar,
        col2X,
        paramsY - 15,
        valueOffset,
        'showParamsAndCosts',
      );
      paramsY += 5;
    }

    if (this.shouldShowField('showObservation')) {
      const obs = this.extension.observation?.trim() || 'Sin observaciones';
      this.drawText('Observaciones', col1X, paramsY + 5, {
        size: 12,
        bold: true,
      });
      this.drawText(obs, col1X, paramsY + 13, { size: 9 });
    }

    let finalY = paramsY;
    if (this.shouldShowField('showObservation')) {
      finalY = paramsY + 25;
    }
    this.summaryBottomY = finalY + 8;
  }

  protected addContentSection(): void {
    const col1X = 10;
    let y = this.summaryBottomY;
    const pageWidth = this.doc.internal.pageSize.getWidth();
    const margin = 10;

    const d = this.extension.documentDetail;
    if (
      this.shouldShowField('showDocumentFinancials') &&
      d != null &&
      (d.startingPaymentDate != null ||
        d.paymentDate != null ||
        d.amount != null ||
        d.amountConciliated != null ||
        d.totalRemaining != null)
    ) {
      this.drawText('Montos del documento', col1X, y, { size: 12, bold: true });
      y += 8;
      if (d.startingPaymentDate != null) {
        const v = formatDateString(String(d.startingPaymentDate), 'dd-mm-yyyy');
        this.drawKeyValue('Liberación original', v, col1X, y, 45);
        y += 5;
      }
      if (d.paymentDate != null) {
        const v = formatDateString(String(d.paymentDate), 'dd-mm-yyyy');
        this.drawKeyValue('Liberación actual', v, col1X, y, 45);
        y += 5;
      }
      if (d.amount != null) {
        this.drawKeyValue(
          'Monto documento',
          formatMoney(d.amount),
          col1X,
          y,
          45,
        );
        y += 5;
      }
      if (d.amountConciliated != null) {
        this.drawKeyValue(
          'Abonos',
          formatMoney(d.amountConciliated),
          col1X,
          y,
          45,
        );
        y += 5;
      }
      if (d.totalRemaining != null) {
        this.drawKeyValue(
          'Saldo deuda',
          formatMoney(d.totalRemaining),
          col1X,
          y,
          45,
        );
        y += 5;
      }
      y += 8;
    }

    const accounts = this.extension.pendingAccounts ?? [];
    if (this.shouldShowField('showPendingAccounts') && accounts.length > 0) {
      this.drawText('Cuentas por cobrar', col1X, y, { size: 12, bold: true });
      y += 8;
      autoTable(this.doc, {
        startY: y,
        margin: { left: margin, right: margin },
        tableWidth: pageWidth - 2 * margin,
        head: [
          [
            'Folio',
            'RUT deudor',
            'Monto documento',
            'Observación',
            'Emisión documento',
            'Fecha de pago',
            'Estado',
          ],
        ],
        body: accounts.map((row) => {
          const observation =
            row.observation != null && row.observation !== ''
              ? row.observation
              : '-';
          return [
            row.documentFolio ?? '-',
            row.documentStakeholderIdentifier ?? '-',
            row.documentAmount != null ? formatMoney(row.documentAmount) : '-',
            observation,
            this.formatPendingAccountDate(row.documentIssuedDate),
            this.formatPendingAccountDate(row.documentPaidAt),
            row.statusLabel ?? '-',
          ];
        }),
        theme: 'grid',
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 7,
        },
        styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
      });
    }
  }
}
